// components/Tabs/web_parts/AgoraTypes.ts
// Agora会議システムで使用する共通型定義

import { IAgoraRTCClient, ILocalVideoTrack } from 'agora-rtc-sdk-ng';

/**
 * リモートユーザーの型定義
 * AgoraRTCから取得されるリモートユーザー情報
 */
export type RemoteUser = ReturnType<typeof import('agora-rtc-sdk-ng').default.createClient>['remoteUsers'][number];

/**
 * 参加者情報の型定義
 */
export interface Participant {
  uid: number;
  name: string;
  originalUid?: any; // デバッグ用に元のUIDも保持
  serialNumber?: string;
  deviceUserName?: string;
}

/**
 * 会議の基本情報型定義
 */
export interface MeetingInfo {
  channelID: string;
  meetingName: string;
  starttime: number;
  durationminute: number;
  regPerson: string;
  deviceName?: string;
  userName?: string;
  serialNumber?: string;
}

/**
 * みまもり会議専用の情報型定義
 */
export interface MimamoriMeetingInfo extends MeetingInfo {
  emergencyID: string;
  limitTime: number;
  flgOpen: number;
  alreadyStarted?: boolean;
}

/**
 * Agoraクライアントの状態管理型定義
 */
export interface AgoraClientState {
  client: IAgoraRTCClient | null;
  localTracks: {
    videoTrack: any;
    audioTrack: any;
  } | null;
  uid: number | null;
  joined: boolean;
  isMuted: boolean;
  isVideoOff: boolean;
}

/**
 * リモートユーザーの状態管理型定義
 */
export interface RemoteUserStates {
  remoteUsers: RemoteUser[];
  speakingUser: string | null;
  remoteAudioStates: { [uid: string]: boolean };
  remoteVideoStates: { [uid: string]: boolean };
}

/**
 * 画面レイアウト関連の型定義
 */
export interface LayoutState {
  width: number;
  height: number;
  isMobilePortrait: boolean;
  isWideLayout: boolean;
  mainHeight: string;
}

/**
 * 退室状態の型定義
 */
export interface ExitState {
  hasLeft: boolean;
  leftReason: 'manual' | 'error';
  shouldPreventAutoJoin: boolean;
}

/**
 * Agora設定の定数
 * APP_ID は環境変数 REACT_APP_AGORA_APP_ID から取得
 */
export const AGORA_CONFIG = {
  APP_ID: process.env.REACT_APP_AGORA_APP_ID ?? '',
  TOKEN: null, // トークン認証を使用しない場合はnull
  CODEC: 'vp8' as const,
  MODE: 'rtc' as const,
} as const;

/**
 * 会議タイプの列挙型
 */
export enum MeetingType {
  STANDARD = 'standard',
  MIMAMORI = 'mimamori'
}

/**
 * UID解析結果の型定義
 */
export interface UidParseResult {
  numericUid: number;
  originalUid: any;
}

/**
 * Firestore コレクション名の型定義
 */
export type FirestoreCollection = 'agoraitems' | 'agora_mimamori' | 'devices';

/**
 * 会議参加時のパラメータ型定義
 */
export interface JoinMeetingParams {
  channelId: string;
  uid: number;
  userName: string;
  meetingType: MeetingType;
}